/**
 * Menu styles — single injected <style> tag with all menu-related CSS
 * classes (v0M3.7). Kept as a raw CSS string rather than CSS-in-JS or a
 * framework — smallest possible surface, single injection point.
 *
 * Palette matches the existing bootstrap console log (`#e6dcc4` /
 * `#0e0b06`) and the STYLE_GUIDE.md base — earth-toned parchment on
 * near-black to sit above the map render aesthetically.
 */

const MENU_CSS = `
.su-menu-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 11, 6, 0.94);
  color: #e6dcc4;
  font-family: 'Trajan Pro', 'Palatino Linotype', Palatino, Georgia, serif;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow: auto;
}

.su-menu-panel {
  background: #241d10;
  border: 1px solid #6a5a36;
  border-radius: 6px;
  padding: 32px 40px;
  min-width: 480px;
  max-width: 90vw;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.6);
}

.su-menu-title {
  font-size: 28px;
  letter-spacing: 0.06em;
  margin: 0 0 24px 0;
  text-align: center;
  color: #c4a874;
}

.su-menu-subtitle {
  font-size: 14px;
  color: #a3906a;
  text-align: center;
  margin: -16px 0 24px 0;
  letter-spacing: 0.04em;
}

.su-menu-button {
  display: block;
  width: 100%;
  padding: 12px 20px;
  margin: 8px 0;
  background: #3a2f1a;
  color: #e6dcc4;
  border: 1px solid #6a5a36;
  border-radius: 4px;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  text-align: left;
}

.su-menu-button:hover {
  background: #4a3d24;
  border-color: #c4a874;
}

.su-menu-button.su-primary {
  background: #6a4a20;
  border-color: #c4a874;
  color: #f5eacf;
  font-weight: bold;
  text-align: center;
}

.su-menu-button.su-primary:hover {
  background: #7a5828;
}

.su-menu-button.su-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.su-menu-button.su-disabled:hover {
  background: #3a2f1a;
  border-color: #6a5a36;
}

.su-menu-back {
  font-size: 14px;
  color: #a3906a;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0 0 16px 0;
  font-family: inherit;
}

.su-menu-back:hover {
  color: #c4a874;
  text-decoration: underline;
}

.su-menu-banner {
  padding: 10px 14px;
  margin: 0 0 16px 0;
  background: #3a2515;
  color: #f4c584;
  border-left: 3px solid #c4a874;
  font-size: 14px;
  border-radius: 2px;
}

.su-menu-error {
  background: #3a1515;
  color: #f4a484;
  border-left-color: #c47474;
}

.su-menu-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin: 10px 0;
}

.su-menu-label {
  min-width: 130px;
  font-size: 14px;
  color: #a3906a;
}

.su-menu-select, .su-menu-number {
  flex: 1;
  padding: 6px 10px;
  background: #1a140a;
  color: #e6dcc4;
  border: 1px solid #6a5a36;
  border-radius: 3px;
  font-family: inherit;
  font-size: 14px;
}

.su-menu-select:focus, .su-menu-number:focus {
  outline: none;
  border-color: #c4a874;
}

.su-menu-slot-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 12px 0 20px 0;
}

.su-menu-slot-card {
  background: #1a140a;
  border: 1px solid #4a3d24;
  border-radius: 4px;
  padding: 12px;
}

.su-menu-slot-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  margin: 0 0 8px 0;
  color: #e6dcc4;
}

.su-menu-slot-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.su-menu-role-row {
  display: flex;
  gap: 4px;
}

.su-menu-role-pill {
  flex: 1;
  padding: 5px 8px;
  background: #241d10;
  color: #a3906a;
  border: 1px solid #4a3d24;
  border-radius: 2px;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  text-align: center;
  letter-spacing: 0.04em;
  transition: background 0.1s, color 0.1s;
}

.su-menu-role-pill:hover {
  color: #e6dcc4;
}

.su-menu-role-pill.su-active {
  background: #6a4a20;
  color: #f5eacf;
  border-color: #c4a874;
}

.su-menu-mode-tile-row {
  display: flex;
  gap: 16px;
  margin: 20px 0;
}

.su-menu-mode-tile {
  flex: 1;
  padding: 32px 20px;
  background: #3a2f1a;
  border: 2px solid #6a5a36;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  text-align: center;
  font-family: inherit;
  color: inherit;
}

.su-menu-mode-tile:hover {
  background: #4a3d24;
  border-color: #c4a874;
}

.su-menu-mode-tile.su-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.su-menu-mode-tile.su-disabled:hover {
  background: #3a2f1a;
  border-color: #6a5a36;
}

.su-menu-mode-tile-title {
  font-size: 22px;
  color: #c4a874;
  margin: 0 0 6px 0;
}

.su-menu-mode-tile-desc {
  font-size: 13px;
  color: #a3906a;
  margin: 0;
}

.su-menu-hidden {
  display: none !important;
}

/* Speed HUD (v0M3.7b) — fixed top-right pill row above the canvas. */
.su-speed-hud {
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(14, 11, 6, 0.82);
  border: 1px solid #6a5a36;
  border-radius: 4px;
  padding: 6px 12px;
  color: #e6dcc4;
  font-family: 'Trajan Pro', 'Palatino Linotype', Palatino, Georgia, serif;
  z-index: 500;
  user-select: none;
}

.su-speed-hud-day {
  font-size: 14px;
  letter-spacing: 0.06em;
  color: #c4a874;
  min-width: 78px;
}

.su-speed-hud-buttons {
  display: flex;
  gap: 4px;
}

.su-speed-hud-btn {
  min-width: 40px;
  padding: 4px 10px;
  background: #241d10;
  color: #a3906a;
  border: 1px solid #4a3d24;
  border-radius: 3px;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
}

.su-speed-hud-btn:hover {
  color: #e6dcc4;
  border-color: #6a5a36;
}

.su-speed-hud-btn.su-active {
  background: #6a4a20;
  color: #f5eacf;
  border-color: #c4a874;
}

/* Resource HUD (v0M3.9) — fixed top-left row above the canvas. */
.su-resource-hud {
  position: fixed;
  top: 16px;
  left: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgba(14, 11, 6, 0.82);
  border: 1px solid #6a5a36;
  border-radius: 4px;
  padding: 6px 14px;
  color: #e6dcc4;
  font-family: 'Trajan Pro', 'Palatino Linotype', Palatino, Georgia, serif;
  z-index: 500;
  user-select: none;
}

.su-resource-cell {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 13px;
}

.su-resource-name {
  color: #a3906a;
  letter-spacing: 0.04em;
  font-size: 12px;
  text-transform: uppercase;
}

.su-resource-amount {
  color: #f5eacf;
  font-weight: bold;
  font-size: 14px;
  min-width: 28px;
  text-align: right;
}

.su-resource-income {
  font-size: 11px;
  min-width: 46px;
}

.su-resource-income.su-positive {
  color: #a3c47a;
}

.su-resource-income.su-negative {
  color: #d47a5a;
}

.su-resource-income.su-neutral {
  color: #6a5a36;
}

/* Victory banner (v0M3.9) — full-screen overlay when the game ends. */
.su-victory-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 11, 6, 0.88);
  color: #e6dcc4;
  font-family: 'Trajan Pro', 'Palatino Linotype', Palatino, Georgia, serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 900;
  text-align: center;
  gap: 16px;
}

.su-victory-title {
  font-size: 42px;
  letter-spacing: 0.08em;
  color: #c4a874;
  margin: 0;
}

.su-victory-subtitle {
  font-size: 18px;
  color: #a3906a;
  margin: 0;
  letter-spacing: 0.04em;
}

/* Victory banner buttons (v0M15.6) — horizontal row below the subtitle. */
.su-victory-buttons {
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 24px;
  justify-content: center;
  flex-wrap: wrap;
}

.su-victory-btn {
  padding: 12px 24px;
  background: #3a2f1a;
  color: #e6dcc4;
  border: 1px solid #6a5a36;
  border-radius: 4px;
  font-family: inherit;
  font-size: 15px;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  min-width: 200px;
}

.su-victory-btn:hover {
  background: #4a3d24;
  border-color: #c4a874;
}

/* Settlement panel (v0M3.10) — modal DOM overlay when a player-owned city is clicked. */
.su-city-panel-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(14, 11, 6, 0.55);
  z-index: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.su-city-panel {
  background: #241d10;
  border: 1px solid #6a5a36;
  border-radius: 6px;
  min-width: 520px;
  max-width: 92vw;
  color: #e6dcc4;
  font-family: 'Trajan Pro', 'Palatino Linotype', Palatino, Georgia, serif;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.6);
}

.su-city-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #4a3d24;
}

.su-city-panel-title {
  font-size: 20px;
  color: #c4a874;
  margin: 0;
  letter-spacing: 0.05em;
}

.su-city-panel-close {
  background: transparent;
  color: #a3906a;
  border: none;
  cursor: pointer;
  font-size: 20px;
  font-family: inherit;
  line-height: 1;
  padding: 2px 8px;
}

.su-city-panel-close:hover {
  color: #f5eacf;
}

.su-city-panel-tabs {
  display: flex;
  gap: 2px;
  padding: 0 12px;
  background: #1a140a;
  border-bottom: 1px solid #4a3d24;
}

.su-city-panel-tab {
  padding: 10px 20px;
  background: transparent;
  color: #a3906a;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.su-city-panel-tab:hover {
  color: #e6dcc4;
}

.su-city-panel-tab.su-active {
  color: #f5eacf;
  border-bottom-color: #c4a874;
}

.su-city-panel-body {
  padding: 16px 20px;
  max-height: 520px;
  overflow-y: auto;
}

.su-city-panel-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid #4a3d24;
  border-radius: 3px;
  margin-bottom: 6px;
  background: #1a140a;
}

.su-city-panel-row.su-disabled {
  opacity: 0.5;
}

.su-city-panel-item-name {
  flex: 1;
  color: #e6dcc4;
  font-size: 14px;
}

.su-city-panel-item-cost {
  color: #a3906a;
  font-size: 12px;
}

.su-city-panel-action {
  padding: 6px 14px;
  background: #6a4a20;
  color: #f5eacf;
  border: 1px solid #c4a874;
  border-radius: 3px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
}

.su-city-panel-action:hover {
  background: #7a5828;
}

.su-city-panel-action:disabled {
  background: #3a2f1a;
  color: #6a5a36;
  border-color: #4a3d24;
  cursor: not-allowed;
}

.su-city-panel-army-select {
  min-width: 140px;
  padding: 4px 8px;
  background: #241d10;
  color: #e6dcc4;
  border: 1px solid #4a3d24;
  border-radius: 3px;
  font-family: inherit;
  font-size: 12px;
}

.su-city-panel-note {
  color: #a3906a;
  font-size: 12px;
  padding: 8px 10px;
  border-left: 2px solid #4a3d24;
  margin: 6px 0;
}

/* Battle-command view (MFix2.3 — was modal at v0M3.5). Full-screen toggle
 * (SG system/galaxy pattern). Backdrop now fills the viewport as an
 * opaque view rather than a translucent overlay; modal fills 100vh × 100vw.
 * Toggle triggers: crossed-swords icon on strategic map (open), X button
 * or Tab key (close). Strategic map hidden while view is active; sim
 * continues in both. */
.su-battle-backdrop {
  position: fixed;
  inset: 0;
  background: #14100a;
  z-index: 800;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.su-battle-modal {
  background: #241d10;
  color: #e6dcc4;
  font-family: 'Trajan Pro', 'Palatino Linotype', Palatino, Georgia, serif;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.su-battle-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 22px;
  border-bottom: 1px solid #4a3d24;
}

.su-battle-vs {
  font-size: 20px;
  color: #c4a874;
  margin: 0;
  letter-spacing: 0.06em;
}

.su-battle-phase {
  font-size: 15px;
  color: #f4c584;
  margin: 0 20px;
}

.su-battle-phase-timer {
  color: #f5eacf;
  font-weight: bold;
}

.su-battle-body {
  display: grid;
  grid-template-columns: 220px 1fr 260px;
  gap: 0;
  min-height: 620px;
}

.su-battle-panel {
  padding: 14px 16px;
  border-right: 1px solid #4a3d24;
  overflow-y: auto;
}

.su-battle-panel-right {
  border-right: none;
  border-left: 1px solid #4a3d24;
}

.su-battle-panel-title {
  font-size: 13px;
  color: #c4a874;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 8px 0;
}

.su-battle-panel-line {
  font-size: 13px;
  color: #e6dcc4;
  margin: 4px 0;
}

.su-battle-panel-label {
  color: #a3906a;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 12px 0 4px 0;
}

/* v0M4.1.1 — Combat log section */
.su-combat-log {
  margin-bottom: 12px;
  border-bottom: 1px solid #4a3d24;
  padding-bottom: 8px;
}
.su-combat-log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: #c4a874;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-bottom: 4px;
}
.su-combat-log-toggle {
  background: none;
  border: none;
  color: #c4a874;
  cursor: pointer;
  padding: 0 4px;
  font-size: 13px;
  line-height: 1;
}
.su-combat-log-toggle:hover {
  color: #f0e6ce;
}
.su-combat-log-list {
  max-height: 180px;
  overflow-y: auto;
  font-size: 11px;
  line-height: 1.35;
  padding-right: 4px;
}
.su-combat-log-row {
  display: flex;
  gap: 4px;
  padding: 2px 0;
  border-top: 1px solid rgba(74, 61, 36, 0.35);
}
.su-combat-log-row:first-child {
  border-top: none;
}
.su-combat-log-icon {
  flex: 0 0 auto;
  width: 14px;
  text-align: center;
  opacity: 0.85;
}
.su-combat-log-text {
  flex: 1 1 auto;
  color: #e6dcc4;
  word-break: break-word;
}
.su-combat-log-round {
  flex: 0 0 auto;
  color: #6a5a3a;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 10px;
}
.su-combat-log-row.severity-rout          { color: #c47474; }
.su-combat-log-row.severity-rout .su-combat-log-text          { color: #c47474; }
.su-combat-log-row.severity-kill          .su-combat-log-text { color: #d4a874; }
.su-combat-log-row.severity-charge        .su-combat-log-text { color: #7a94c4; }
.su-combat-log-row.severity-morale        .su-combat-log-text { color: #c4b874; }
.su-combat-log-row.severity-order         .su-combat-log-text { color: #c4b874; }
.su-combat-log-row.severity-xp            .su-combat-log-text { color: #f4d47a; }
.su-combat-log-row.severity-battle-end    .su-combat-log-text { color: #f0e6ce; font-weight: bold; }

/* v0M4.1.2 — Army stats section + XP bar */
.su-army-section {
  padding-bottom: 10px;
  border-bottom: 1px solid #4a3d24;
  margin-bottom: 12px;
}
.su-army-xp-bar-outer {
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
  margin: 6px 0 3px 0;
}
.su-army-xp-bar-fill {
  height: 100%;
  background: #f4d47a;
  width: 0%;
  transition: width 300ms linear;
}
.su-army-xp-label {
  font-size: 11px;
  color: #a3906a;
  margin: 0 0 6px 0;
}

/* v0M4.1.2 — Cohort inspection: tags line + balance-sheet grid */
.su-cohort-tags {
  color: #c4a874;
  font-size: 11px;
  letter-spacing: 0.02em;
}
.su-battle-panel-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px 8px;
  font-size: 11px;
  color: #e6dcc4;
  margin: 4px 0;
}
.su-battle-panel-stats > div {
  padding: 2px 0;
}

/* MFix2.2 — 60-figure health-tint grid on the right panel */
.su-battle-figure-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 1px;
  margin: 4px 0 10px 0;
  padding: 4px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid #4a3d24;
  border-radius: 2px;
}
/*
 * Session-28 (2026-07-24) — person-icon figures.
 * MFix6.5 (2026-07-23) used shield.svg; Session-28 swaps to person.svg per
 * Michael's playtest ask — the person silhouette reads more like a soldier.
 *
 * Cell renders the person.svg silhouette as a CSS mask over the health-
 * tinted background-color. All 60 figures use the same mask; per-figure
 * state is entirely in the background-color mutation (see renderFigureGrid).
 * Killed figures render solid red (Session-28 colour scheme).
 *
 * aspect-ratio 1:1.
 */
.su-figure-cell {
  aspect-ratio: 1 / 1;
  min-width: 10px;
  min-height: 10px;
  -webkit-mask-image: url('/icons/figures/person.svg');
          mask-image: url('/icons/figures/person.svg');
  -webkit-mask-size: contain;
          mask-size: contain;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  /* Session-28b (2026-07-24): person.svg as-shipped by Michael followed
     the Game-Icons.net convention of a black backdrop rectangle + white
     silhouette. Under the default alpha mask-mode both paths counted as
     opaque, so the whole cell rendered as a coloured square. Fix: the
     SVG was rewritten as a solid-black silhouette on a transparent
     background (matches the shield.svg pattern) so default masking
     produces a person-shaped tinted region. */
  transition: background-color 200ms linear, opacity 200ms linear;
}

/* v0M4.1.3 — Phase strip under the modal header */
.su-battle-phase-strip {
  display: flex;
  gap: 6px;
  padding: 6px 14px 8px 14px;
  border-bottom: 1px solid #4a3d24;
  background: rgba(20, 16, 10, 0.6);
}
.su-phase-cell {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.su-phase-cell-label {
  font-size: 11px;
  color: #a3906a;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0 0 3px 0;
  text-align: center;
}
.su-phase-cell-bar-outer {
  height: 4px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  overflow: hidden;
}
.su-phase-cell-bar-fill {
  height: 100%;
  background: #6a5a3a;
  width: 0%;
  transition: width 200ms linear;
}
.su-phase-cell.su-phase-active .su-phase-cell-label {
  color: #f4d47a;
}
.su-phase-cell.su-phase-active .su-phase-cell-bar-fill {
  background: #f4d47a;
}

.su-battle-canvas-wrap {
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a140a;
}

.su-battle-canvas {
  background: #14100a;
  border: 1px solid #4a3d24;
  cursor: crosshair;
}

.su-battle-order-btn {
  display: block;
  width: 100%;
  padding: 8px 12px;
  margin: 6px 0;
  background: #3a2f1a;
  color: #e6dcc4;
  border: 1px solid #6a5a36;
  border-radius: 3px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  text-align: left;
}

.su-battle-order-btn:hover {
  background: #4a3d24;
  border-color: #c4a874;
}

.su-battle-order-btn.su-active {
  background: #6a4a20;
  color: #f5eacf;
  border-color: #c4a874;
}

/* v0M4.0h.2 — disabled state for Commander's Orders during Phase 1 Setup. */
.su-battle-order-btn:disabled {
  background: #241d10;
  color: #6a5a36;
  border-color: #3a2f1a;
  cursor: not-allowed;
}
.su-battle-order-btn:disabled:hover {
  background: #241d10;
  border-color: #3a2f1a;
}

/* Unit portrait inset — Game-Icons.net SVG tinted per faction (v0M3.5c). */
.su-battle-portrait {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #1a140a;
  border: 1px solid #4a3d24;
  border-radius: 4px;
  margin: 8px 0 12px 0;
}

.su-battle-portrait-icon {
  width: 78%;
  height: 78%;
  /* SVG becomes a mask cutout, div background provides the faction color. */
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: contain;
  mask-size: contain;
}

.su-battle-portrait-fallback {
  color: #a3906a;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 44px;
  font-weight: bold;
}

/* Cohort inset (v0M3.5c) — full-width row below the tactical grid showing
   each of the 60 individual soldier icons at their own figure size,
   colored by faction and dimmed / hidden per wound state. */
.su-battle-inset {
  padding: 14px 16px 18px;
  border-top: 1px solid #4a3d24;
  background: #1a140a;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.su-battle-inset-title {
  font-size: 13px;
  color: #a3906a;
  letter-spacing: 0.05em;
  margin: 0 0 12px 0;
  text-align: center;
}

.su-battle-inset-name {
  color: #f5eacf;
  font-weight: bold;
  font-size: 16px;
  letter-spacing: 0.03em;
  text-transform: none;
  margin-right: 10px;
}

.su-battle-inset-counts {
  color: #a3906a;
  font-size: 13px;
}

.su-battle-inset-formation {
  display: grid;
  gap: 4px;
  justify-content: center;
}

.su-battle-inset-figure {
  border-radius: 3px;
  background-color: #6a5a36;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-size: 82%;
  mask-size: 82%;
  transition: opacity 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1a140a;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-weight: bold;
}

.su-battle-inset-figure.su-fallback {
  -webkit-mask-image: none;
  mask-image: none;
}

/* Save/Load slot picker (v0M15.2) — modal listing the 5 named slots + autosave.
   Shared between Save mode (in-game) and Load mode (Main Menu / post-victory). */
.su-save-picker-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 4px 0 20px 0;
  max-height: 60vh;
  overflow-y: auto;
}

.su-save-picker-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #1a140a;
  border: 1px solid #4a3d24;
  border-radius: 4px;
}

.su-save-picker-row.su-autosave {
  border-color: #6a5a36;
  background: #1e180e;
}

.su-save-picker-slot-label {
  min-width: 92px;
  color: #c4a874;
  font-size: 14px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.su-save-picker-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.su-save-picker-name {
  color: #f5eacf;
  font-size: 15px;
  font-weight: bold;
  letter-spacing: 0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.su-save-picker-name.su-empty {
  color: #6a5a36;
  font-weight: normal;
  font-style: italic;
}

.su-save-picker-meta {
  color: #a3906a;
  font-size: 12px;
  letter-spacing: 0.02em;
}

.su-save-picker-name-input {
  flex: 1;
  padding: 6px 10px;
  background: #241d10;
  color: #e6dcc4;
  border: 1px solid #4a3d24;
  border-radius: 3px;
  font-family: inherit;
  font-size: 14px;
  min-width: 0;
}

.su-save-picker-name-input:focus {
  outline: none;
  border-color: #c4a874;
}

.su-save-picker-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.su-save-picker-btn {
  padding: 6px 14px;
  background: #3a2f1a;
  color: #e6dcc4;
  border: 1px solid #6a5a36;
  border-radius: 3px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  letter-spacing: 0.03em;
}

.su-save-picker-btn:hover {
  background: #4a3d24;
  border-color: #c4a874;
}

.su-save-picker-btn.su-primary {
  background: #6a4a20;
  color: #f5eacf;
  border-color: #c4a874;
  font-weight: bold;
}

.su-save-picker-btn.su-primary:hover {
  background: #7a5828;
}

.su-save-picker-btn.su-danger {
  background: #3a2515;
  color: #f4a484;
  border-color: #c47474;
}

.su-save-picker-btn.su-danger:hover {
  background: #4a2f1a;
  border-color: #f4a484;
}

.su-save-picker-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.su-save-picker-btn:disabled:hover {
  background: #3a2f1a;
  border-color: #6a5a36;
}

.su-save-picker-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.su-save-picker-empty-note {
  color: #a3906a;
  font-size: 13px;
  padding: 24px 12px;
  text-align: center;
  font-style: italic;
}

/* Settings modal (v0M15.4) — Audio + Autosave sections. */
.su-settings-section {
  margin: 8px 0 18px 0;
  padding: 12px 14px;
  background: #1a140a;
  border: 1px solid #4a3d24;
  border-radius: 4px;
}

.su-settings-section-title {
  font-size: 13px;
  color: #c4a874;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 12px 0;
}

.su-settings-row {
  display: grid;
  grid-template-columns: 160px 1fr 56px;
  gap: 14px;
  align-items: center;
  margin: 8px 0;
}

.su-settings-row-label {
  color: #a3906a;
  font-size: 13px;
}

.su-settings-row-value {
  color: #f5eacf;
  font-size: 13px;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  text-align: right;
}

.su-settings-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  background: #4a3d24;
  border-radius: 2px;
  outline: none;
  cursor: pointer;
}
.su-settings-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  background: #c4a874;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}
.su-settings-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  background: #c4a874;
  border-radius: 50%;
  cursor: pointer;
  border: none;
}
.su-settings-slider:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.su-settings-checkbox {
  width: 16px;
  height: 16px;
  accent-color: #c4a874;
  cursor: pointer;
}

.su-settings-note {
  color: #6a5a36;
  font-size: 11px;
  font-style: italic;
  margin: 4px 0 0 160px;
  letter-spacing: 0.02em;
}

/* v0M15.4b (2026-07-30) — keybinding-badge pill next to each rebindable
   action row. Fixed width so buttons align across rows even when the
   bound label varies ("Space" vs "F12"). */
.su-keybind-badge {
  display: inline-block;
  min-width: 60px;
  padding: 4px 10px;
  margin: 0 8px;
  background: #2a2416;
  color: #f4d47a;
  border: 1px solid #6a5238;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  text-align: center;
}
`;

/**
 * Idempotently inject the menu stylesheet into <head>. Safe to call
 * multiple times — subsequent calls are no-ops.
 */
export function injectMenuStyles(): void {
  const id = 'su-menu-styles';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = MENU_CSS;
  document.head.appendChild(style);
}
